import { useGlobalRouter } from "feat/globalRouter/useGlobalRouter";
import type { RoutePath } from "./registry";
import { interpolate, type BuildArgs } from "./route";

/**
 * Build a concrete URL from a registered path template and its params. The path is
 * a member of the registry's path union (autocompleted, typo-checked) and the
 * params are derived from it, so no `routes` import is needed.
 *
 * ```ts
 * buildPath("/chat/:type/:roomId", { type: "channel", roomId });
 * buildPath("/account");
 * ```
 */
export function buildPath<P extends RoutePath>(path: P, ...args: BuildArgs<P>): string {
  return interpolate(path, (args[0] ?? {}) as Record<string, string | number>);
}

/**
 * Typed navigation over the existing global router, which keeps View Transitions
 * working. Pass a path string, not a `routes` object.
 *
 * ```ts
 * const router = useRouter();
 * router.push("/chat/:type/:roomId", { type, roomId });
 * router.push("/account");
 * router.back();
 * ```
 */
export function useRouter() {
  const { pushPath, replacePath, goBack } = useGlobalRouter();

  return {
    push: <P extends RoutePath>(path: P, ...args: BuildArgs<P>) => pushPath(buildPath(path, ...args)),
    replace: <P extends RoutePath>(path: P, ...args: BuildArgs<P>) => replacePath(buildPath(path, ...args)),
    back: (useRight?: boolean) => goBack(useRight),
  };
}
